/**
 * How the pieces move, on the board the famous games are played on.
 *
 * WHO THIS PAGE IS FOR, AND WHY IT IS NOT THE OTHER PAGES
 * Everything else on this site assumes you play chess. The library assumes you
 * want to watch a game, the puzzles assume you can see a mate coming, and Study
 * — which was called Learn until this page existed, and is where the confusion
 * came from — is 131 master games annotated move by move, which is a thing you
 * read AFTER you know what a bishop does. Somebody who has never played had
 * nowhere to start, and the nav told them the wrong door was theirs.
 *
 * THE ONE IDEA
 * A beginner's question is never "what is the rule", it is "what can THIS man do,
 * from HERE". The board already answers that: `table.js` lights every legal
 * destination the moment a man is picked up, because that is what it does for
 * somebody playing a game. So this page does not explain the rules and then show
 * a diagram of them — it picks the man up FOR you, and the sentence in the panel
 * describes what is already lit on the board in front of you. The lesson and the
 * board cannot disagree, because the board is the lesson.
 *
 * IT IS THE SAME BOARD AS THE PLAY AND PUZZLE PAGES, deliberately and by import.
 * `table.js` asks its host four questions — may a man be picked up, one has been
 * picked up, here is the move that was chosen, and here is what the line under the
 * board now says. A lesson is a particular set of answers: yes to the first, "say
 * what that man is" to the second, "play it, then say what just happened" to the
 * third, and "read it out" to the fourth. `onLift` and `onSay` are the two things
 * this page needed that the board did not already have, and each is a few lines in
 * `table.js` in the shape the first two were written in.
 *
 * WHY THERE IS A VOICE, AND WHAT IT IS ALLOWED TO SAY
 * The difficulty this page was built to solve — the sentence and the board being
 * in two places — is only half solved by putting them side by side. Reading the
 * sentence still means looking away from the lit squares it is about. So the panel
 * can be spoken, and then the eyes never have to leave the board. `voice.js` says
 * why that is the Web Speech API and not recorded narration.
 *
 * The rule is that the voice says THE PANEL'S OWN STRINGS. `narrate` below is the
 * only thing on the page that talks to it, it is called from the same places that
 * write the panel, and it is handed the same values that were just written there.
 * A second script for the ear would be a third thing that could disagree with the
 * board, and it would disagree silently — nothing on the screen changes when the
 * voice starts saying nine squares over eight lit ones. The whole point of this
 * page is that its claims are the board's claims, and the voice inherits that or
 * it does not get to speak.
 *
 * WHY YOU CAN WANDER OFF THE LESSON
 * `canPick` is true for anything of the side to move, not only for the man the
 * lesson is about, and after a move the side to move becomes whoever's turn it
 * now is — so the board is a free sandbox with a lesson pointing at part of it.
 * A page about how the pieces move that answered only for the one piece it had
 * chosen would be refusing the exact question it exists for. `PIECE_LINES` is
 * what it says when you pick up something else.
 */

import { createTable, escapeHtml } from '../play/table.js';
import { squareIndex, NAMES, typeOf } from '../../animations/207-famous-chess-games/chess.js';
import { mountNav } from '../nav.mjs';
import { mountInstall } from '../install.js';
import { count } from '../analytics.js';
import { LESSONS, PIECE_LINES } from './lessons.js';
import { createVoice } from '../voice.js';
import { onMerged, read as readProgress, syncSoon, write as writeProgress } from '../progress.js';
import { mountLeanLayout } from '../lean-layout.js';

const $ = (id) => document.getElementById(id);

/**
 * WHAT EACH LESSON LOOKS LIKE: its man, drawn. The four that are not one man are
 * the two that meet in them — the king and the rook that castle, the two pawns
 * of en passant, the pawn and the queen it becomes, and the king for check. The
 * text-style selector (U+FE0E) keeps a phone from swapping in a coloured emoji.
 * Decorative: the name beside it is what a screen reader reads.
 */
/*
 * The six men as silhouettes, drawn here rather than typed as ♟ ♞ ♝: the chess
 * glyphs are whatever a device's fonts make of them — a good bishop on one, a
 * blob on the next, an emoji on a phone — and these are the same everywhere and
 * take the page's colours. `.cut` is the slit and the eye, drawn in the tile's
 * ground so they read as holes (see `learn.css`).
 */
const MEN = {
  p: '<svg viewBox="0 0 45 45" aria-hidden="true"><circle cx="22.5" cy="12" r="5.5"/><path d="M17 20h11l-1.5 3.5c3.5 3 5.5 7.5 5.5 12.5H13c0-5 2-9.5 5.5-12.5z"/><rect x="11" y="35" width="23" height="5" rx="2"/></svg>',
  r: '<svg viewBox="0 0 45 45" aria-hidden="true"><path d="M12 8h5v3.5h3.5V8h4v3.5H28V8h5v8l-3.5 3v12l3.5 3.5V36H12v-1.5L15.5 31V19L12 16z"/><rect x="10" y="35" width="25" height="5" rx="2"/></svg>',
  n: '<svg viewBox="0 0 45 45" aria-hidden="true"><path d="M14 35c0-8 4-12 9-15.5-2.5.5-5.5 2-7.5 2.5L12 18c1-6 7-11 13-12l1.5-3.5 3 4.5c5 3.5 7 10 7 17V35z"/><circle class="cut" cx="21" cy="12.5" r="1.6"/><rect x="11" y="35" width="25" height="5" rx="2"/></svg>',
  b: '<svg viewBox="0 0 45 45" aria-hidden="true"><circle cx="22.5" cy="7.5" r="3"/><path d="M22.5 11C16.5 14.5 14 19 14 23c0 3.5 2.5 6 5 7h-2.5v4h12v-4H26c2.5-1 5-3.5 5-7 0-4-2.5-8.5-8.5-12z"/><path class="cut" d="M20 17.5l6 6"/><rect x="11" y="34.5" width="23" height="5.5" rx="2"/></svg>',
  q: '<svg viewBox="0 0 45 45" aria-hidden="true"><circle cx="8.5" cy="13" r="2.8"/><circle cx="15.5" cy="10" r="2.6"/><circle cx="22.5" cy="8.5" r="2.8"/><circle cx="29.5" cy="10" r="2.6"/><circle cx="36.5" cy="13" r="2.8"/><path d="M9 16l5 15h17l5-15-7.5 6.5-6-11.5-6 11.5z"/><rect x="11.5" y="32" width="22" height="8" rx="2.5"/></svg>',
  k: '<svg viewBox="0 0 45 45" aria-hidden="true"><path d="M21 3h3v4h4v3h-4v4h-3v-4h-4V7h4z"/><path d="M22.5 15.5c-7.5 0-12.5 4-12.5 9.5 0 4 3 7.5 5.5 9.5h14c2.5-2 5.5-5.5 5.5-9.5 0-5.5-5-9.5-12.5-9.5z"/><rect x="11.5" y="34" width="22" height="6" rx="2"/></svg>',
};
const LESSON_MEN = {
  pawn: 'p', knight: 'n', bishop: 'b', rook: 'r', queen: 'q', king: 'k',
  castling: 'kr', 'en-passant': 'pp', promotion: 'pq', checkmate: 'k',
};
const menOf = (id) => [...(LESSON_MEN[id] || '')]
  .map((m) => `<span class="man">${MEN[m]}</span>`).join('');


// The nav first, before the wait for three.js: it is the one thing on this page
// that is about the site rather than about the board, and somebody who lands here
// and decides they would rather watch a game should not have to wait for a WebGL
// context to be told where the library is.
mountNav($('nav-slot'), { here: 'learn', base: '../' });
mountInstall($('install-slot'), { base: '../' });

/**
 * Which lessons have been done, in `localStorage` under the same `immortal-`
 * prefix as the theme, the page size and the solved puzzles.
 *
 * "Done" means the visitor moved the man the lesson is about, which is the only
 * evidence this page can have that anything was understood — and it is decent
 * evidence, because the move cannot be made without picking the right man up and
 * choosing one of the squares that lit.
 */
const KEY = 'immortal-lessons';
const done = new Set(readDone());

// Kept here and on the account — see `chess-app/progress.js`. Lessons merge by
// UNION, so one finished on a phone stays finished on a laptop.
function readDone() {
  try { return JSON.parse(readProgress(KEY) || '[]'); } catch { return []; }
}
function save() {
  writeProgress(KEY, JSON.stringify([...done]));
}

/**
 * How far above the board the lessons are seen from, in degrees.
 *
 * Chosen by looking: at the play pages' 32 the dots on e3 and e4 are behind the
 * pawn on e2, and at the Top preset's 86 the board is a diagram and the men stop
 * being pieces. This is the lowest angle at which every mark in all ten lessons —
 * the rook's nine, worst of them — is visible without turning the board.
 */
const LOOK_DOWN = 52;

/** Which lesson is on the board, and whether its move has been made yet. */
let at = 0;
let moved = false;
/** Whether Start has been pressed (or anything else that runs a lesson). */
let started = false;

const now = () => LESSONS[at];

// Before the board, not after: this is a list of ten words that needs no WebGL,
// and a beginner staring at "building the scene" should be able to see what the
// ten lessons are while it builds.
renderList();

// ------------------------------------------------------------------ the voice

/**
 * Whether the page reads itself out, in `localStorage` beside the theme and the
 * ten ticks.
 *
 * OFF UNTIL ASKED, and it is worth saying why given how much of this page argues
 * for the voice. A site that starts talking when a link is opened is a site people
 * close, and a beginner is often on a page like this precisely because they are
 * somewhere they cannot make a noise — on a train, in an office, next to somebody
 * asleep. The default is the quiet one and the offer is one click wide.
 *
 * Remembered, though, so that somebody who has decided they want to be talked to
 * does not have to decide it again on lesson two. See the autoplay note in
 * `voice.js` for what that costs on Safari and how it is paid.
 */
const VOICE_KEY = 'immortal-voice';

/**
 * Built before the board, because it is the one control on this page that does not
 * need one — and a visitor who came here to be READ to should not have to wait for
 * a WebGL context before they can find out that they can be.
 */
const voice = createVoice({ onChange: paintVoice });

if (voice.available) {
  $('voice').hidden = false;
  $('voice-key').hidden = false;
  // On unless it was turned off — the same default as the player, because this
  // is one preference about a person and not two facts about two pages.
  try { voice.on = localStorage.getItem(VOICE_KEY) !== 'off'; } catch { /* private mode */ }
  paintVoice({ on: voice.on, speaking: false });
}

$('voice').addEventListener('click', () => setVoice(!voice.on));

/** Turn it on or off, remember which, and count it. */
function setVoice(want) {
  voice.on = want;
  try { localStorage.setItem(VOICE_KEY, want ? 'on' : 'off'); } catch { /* private mode */ }
  count('voice', want ? 'on' : 'off');
}

/**
 * The button, in its three states: off, on, and talking.
 *
 * The waves are drawn and hidden rather than added and removed, so the icon is one
 * shape at one width in all three states — a speaker that grew waves would move
 * the two words beside it every time a sentence began. `learn.css` fades them.
 */
function paintVoice({ on, speaking }) {
  const button = $('voice');
  if (!button) return;
  button.setAttribute('aria-pressed', on ? 'true' : 'false');
  button.classList.toggle('saying', Boolean(on && speaking));
  button.title = on ? 'Stop reading aloud (v)' : 'Read the lessons aloud (v)';
  $('voice-icon').innerHTML = '<path d="M2.5 6.2h2.2L8 3.4v9.2L4.7 9.8H2.5z"></path>'
    + (on
      ? '<path class="wave" d="M10.4 5.9a3 3 0 0 1 0 4.2"></path>'
        + '<path class="wave" d="M12.4 4.1a5.6 5.6 0 0 1 0 7.8"></path>'
      : '<path d="M10.8 6.2l3.2 3.6M14 6.2l-3.2 3.6"></path>');
}

const table = await createTable();
// 2D / 3D in the masthead, before Install (`../lean-layout.js`), and no camera
// menu at all: a lesson needs the board seen one way, and anybody who wants
// another angle can still drag it round. The six angles are hidden in `learn.css`.
mountLeanLayout();

const live = table.live;

// Anything of the side to move, all the time. See WHY YOU CAN WANDER OFF THE
// LESSON above — this is the page's one substantive difference from the puzzles.
table.canPick = () => true;
// A see-through copy of the man goes out to every lit square, on the path the
// real one would take. The dots say where; the ghost says how — that the rook
// slides and the knight turns a corner — without a word of it in the panel.
table.ghost = true;
table.onLift = (square, piece) => lifted(square, piece);
table.onPick = (mv) => { played(mv); };
// There is no opponent on this page and the material line would be claiming one.
table.opponentName = 'The other side';

/**
 * Whatever the board says under itself, said out loud.
 *
 * The sentence that matters here comes from inside `table.js` and not from this
 * file: "that man has nowhere to go", said when a beginner picks up a rook in the
 * opening. It is one of the most useful things this page can say — they are waiting
 * for squares to light and none are coming — and nothing in `learn.js` knows it
 * happened.
 *
 * COALESCED TO THE END OF THE TASK, and that is not tidiness, it is the fix for a
 * stutter you can hear. One click can set this line twice: `table.js` says "nowhere
 * to go" as it marks the board, and then `onLift` hands the page the same lift and
 * `lifted` writes the piece's own description over the top of it — deliberately,
 * and there is a comment in `table.js` saying so. On the screen that is one line
 * being corrected before anybody reads it. Spoken immediately it is half a word and
 * then a cancel.
 *
 * So the text is held and one flush is booked. A second `say` in the same task
 * replaces it rather than interrupting it, and what is spoken is what the panel
 * ended up showing — which is the rule the whole page is built on.
 *
 * The empty string is a value here and not a skip: `say('')` is how the line is
 * CLEARED, and it has to be able to cancel a queued sentence that is no longer on
 * the screen. `show` relies on it — it clears the line, then narrates the lesson,
 * and the flush that follows must find nothing left to say.
 */
let pendingSay = null;

table.onSay = (text) => {
  const first = pendingSay === null;
  pendingSay = text;
  if (!first) return;
  queueMicrotask(() => {
    const say = pendingSay;
    pendingSay = null;
    // After whatever is being read, not over it: see `after` in `voice.js`.
    if (say) voice.speak([say], { after: true });
  });
};

/**
 * Which lesson to open on. `?lesson=knight` if a link names one — that is what
 * the homepage's "pick up where you left off" links to (`chess-app/resume.js`),
 * at the first lesson not yet done. Otherwise the first lesson, as it always
 * was: somebody arriving from the nav has asked for the lessons, not for the
 * next one, and the list beside the board shows which are done.
 *
 * An id that names no lesson is ignored rather than guessed at.
 */
const askedFor = new URLSearchParams(location.search).get('lesson');
const openAt = Math.max(0, LESSONS.findIndex((l) => l.id === askedFor));
await show(openAt, { scroll: false, begin: false });

// ---------------------------------------------------------------- the lessons

/**
 * Put a lesson up, and pick its man up.
 *
 * `setScore` rather than a run of `apply` calls, exactly as the puzzles do: it
 * cuts to the position instead of animating a sequence nobody asked to watch —
 * and here the sequence is scaffolding rather than content, so watching it would
 * be actively misleading about what the lesson is.
 *
 * The lift is the last thing and it is the point. `table.click` is the same
 * function a mouse press goes through, so the squares are lit by the same code
 * that lights them for a player; there is no second "highlight" path on this page
 * that could show something different from what clicking would.
 *
 * `begin: false` is the page opening: everything is set up but the lift and the
 * voice, which wait for Start (`start`). See WHY THERE IS A START BUTTON.
 */
async function show(index, { scroll = true, begin = true } = {}) {
  at = ((index % LESSONS.length) + LESSONS.length) % LESSONS.length;
  const lesson = now();
  moved = false;

  table.you = lesson.you;
  table.state = 'playing';
  table.explainMate(false);
  // No arrow for the moves that set a lesson up, unless the lesson says its last
  // one matters — see `lastMove` in `lessons.js`. The visitor's own moves are
  // marked as usual.
  table.quietSetup = !lesson.lastMove;
  await table.setScore(lesson.score);
  // Higher than the play pages sit — see `faceYourSideFrom`. The marks on the
  // destination squares ARE the lesson, and at the angle you would play from,
  // the rank in front of a man is hidden behind the man.
  await table.faceYourSideFrom(LOOK_DOWN, { snap: true });

  $('verdict').hidden = true;
  // Emptied and not merely hidden. It holds the previous lesson's "Next: the
  // knight" button until something replaces it, and a hidden box with a live
  // button in it is one CSS mistake away from offering the wrong lesson.
  $('verdict-acts').innerHTML = '';
  $('lesson-name').textContent = lesson.name;
  $('lesson-line').innerHTML = lessonLine(lesson);
  $('lesson-look').textContent = lesson.look;
  // `moves: false` — see `setPills`. The score got the man here; it is not a game.
  table.setPills([
    `${at + 1} of ${LESSONS.length}`,
    lesson.you === 'w' ? 'You are White' : 'You are Black',
  ], { moves: false });
  markList(scroll);
  countUp();
  if (!begin && !started) {
    table.setLead('Press Start to begin the lesson.');
    $('start').hidden = false;
    return;
  }
  run();
}

/**
 * WHY THERE IS A START BUTTON.
 *
 * The page used to run the first lesson as it loaded: the man picked up, his
 * ghost sent out to every square, the lesson read aloud. The ghost played while
 * the visitor was still finding the board, and on a phone the voice could not
 * start at all — speech wants a tap, and the page had not had one. Start is that
 * tap: one press, and the animation and the voice begin together, when somebody
 * is looking. Anything else that runs a lesson — Next, a lesson chip, Again,
 * their keys — is a tap too, and starts the page the same way. `started` is
 * declared up with `at`, because the first `show` runs before this line does.
 */
/** The first press: take the button away and run the lesson on the board. */
function start() {
  if (started) return;
  started = true;
  run();
}

/** Pick the lesson's man up and read the lesson. */
function run() {
  started = true;
  $('start').hidden = true;
  const lesson = now();
  const instruction = lead();
  table.click(squareIndex(lesson.focus));
  // AFTER the lift, and that ordering is the whole of it. `table.click` clears the
  // line under the board, which reaches `onSay` — so narrating first would mean the
  // lesson being cut off by its own board being set up. It is also the right order
  // for the ear: the squares are already lit by the time anything is said about
  // them, so "e3 and e4" is a description and not a promise.
  narrate([lesson.name, lesson.line, lesson.caveat, lesson.look, instruction]);
}

/**
 * Say these, if the voice is on.
 *
 * The only route to the voice on this page, and it takes STRINGS THAT WERE JUST
 * PUT IN THE PANEL — never a lesson id to look things up from, and never a sentence
 * composed for speaking. See the header. Nulls are dropped so a lesson without a
 * caveat needs no branch at the call site.
 */
function narrate(parts, options) {
  voice.speak(parts.filter(Boolean), options);
}

/**
 * The lesson's own sentences, with the caveat where there is one.
 *
 * The caveat is marked rather than run in with the rest, because it is the one
 * thing on the page that is about the position being artificial and a beginner
 * has no way to tell that for themselves. See `lessons.js`.
 */
function lessonLine(lesson) {
  const body = `<span>${escapeHtml(lesson.line)}</span>`;
  if (!lesson.caveat) return body;
  return `${body}<span class="caveat">${escapeHtml(lesson.caveat)}</span>`;
}

/**
 * The standing instruction under the board.
 *
 * The man is named off the BOARD rather than off the lesson's title, which is the
 * fix for "click one of the lit squares to move the En passant". Three of the ten
 * lessons are named for a move rather than for a piece, and the sentence wants the
 * piece — en passant is a pawn doing something, castling is a king doing something,
 * and a lesson added later will be whatever is standing on its focus square
 * without anybody having to remember to write the noun down twice.
 */
function lead() {
  const text = moved
    ? (at + 1 < LESSONS.length
      ? 'Move anything else you like, or take the next lesson.'
      : 'That is all ten. Play a game.')
    : `Click one of the lit squares to move the ${focusName()}.`;
  table.setLead(text);
  // Handed back as well as set, so `show` and `finish` can put it at the end of
  // what the voice says. Spoken, it is the sentence that turns a lesson from
  // something being read at you into something to do — and `setLead` is exactly
  // the one line on this page that `onSay` never sees, because it is the standing
  // instruction rather than a remark over the top of it.
  return text;
}

/** What the man this lesson is about is called: "pawn", "king", "queen". */
function focusName() {
  const piece = live.pos.at(squareIndex(now().focus));
  return piece ? NAMES[typeOf(piece)] : 'man';
}

// ------------------------------------------------------------- what it answers

/**
 * A man has been picked up.
 *
 * Two cases, and the second is not an error: the lesson's own man, which needs
 * nothing said because the panel is already about it, or some other man, which
 * gets named and described. `piece` is the letter `chess.js` uses — upper case for
 * White — so the line is looked up in lower case and the colour read off the case.
 */
function lifted(square, piece) {
  if (square === squareIndex(now().focus) && !moved) return table.say('');
  const kind = String(piece).toLowerCase();
  const line = PIECE_LINES[kind];
  if (!line) return;
  const side = piece === piece.toUpperCase() ? 'White' : 'Black';
  table.say(`${side}: ${line}.`);
}

/**
 * A move has been chosen. Play it.
 *
 * Everything is played, including a move that has nothing to do with the lesson.
 * There is no wrong answer on this page — a beginner who picks up the queen in
 * the middle of the knight's lesson and moves her across the board has just
 * learnt what a queen does, which is the thing this page is for.
 *
 * `you` follows the turn afterwards, so the sandbox keeps working rather than
 * locking up the moment the lesson's own move has been made.
 */
async function played(mv) {
  const wasFocus = !moved && mv.from === squareIndex(now().focus);
  await table.apply(mv);
  table.you = live.turn;
  // No `showMoves` on purpose: the panel's score box is left hidden on this page,
  // because a list headed "1. h4 h5 2. Rh3" presents the scaffolding that reached
  // the position as a game, and a beginner has no way to know it was not one.
  if (live.outcome) return over();
  if (!wasFocus) return table.say('');
  finish();
}

/** The lesson's own move has been made. */
function finish() {
  const lesson = now();
  moved = true;
  table.say('');
  $('verdict').hidden = false;
  $('verdict-line').textContent = 'That is the move.';
  $('verdict-why').textContent = lesson.done;
  $('verdict-acts').innerHTML = acts();
  if (!done.has(lesson.id)) {
    done.add(lesson.id);
    save();
    count('lesson-done', lesson.id);
  }
  const instruction = lead();
  markList(false);
  countUp();
  // The three lines of the verdict, in the order they are on the screen. The
  // "Next: the knight" button is not among them: a voice that reads the buttons out
  // is a screen reader, and the visitor already has one of those if they want one.
  narrate([$('verdict-line').textContent, lesson.done, instruction], { after: true });
}

/**
 * The game ended, which on this page only the last lesson can do.
 *
 * `explainMate` is the scene's own mating net — the king, and every square he
 * cannot use, drawn on the board. It is the closing shot of every video on the
 * channel, and the first time a beginner sees checkmate is the right moment to
 * show them that a mate is a picture and not a rule.
 */
function over() {
  const lesson = now();
  moved = true;
  if (live.outcome?.by === 'checkmate') table.explainMate(true);
  $('verdict').hidden = false;
  $('verdict-line').textContent = live.outcome?.by === 'checkmate'
    ? 'Checkmate. The game is over.'
    : `That is the game over — ${live.outcome?.by || 'a draw'}.`;
  $('verdict-why').textContent = lesson.done;
  $('verdict-acts').innerHTML = acts();
  if (!done.has(lesson.id)) {
    done.add(lesson.id);
    save();
    count('lesson-done', lesson.id);
  }
  table.setLead('That is all ten. Play a game.');
  markList(false);
  countUp();
  narrate([$('verdict-line').textContent, lesson.done], { after: true });
}

/** Where to go from the end of a lesson. */
function acts() {
  if (at + 1 < LESSONS.length) {
    return `<button class="ctl primary" id="go-next" type="button">Next: ${escapeHtml(LESSONS[at + 1].name)}</button>`;
  }
  return '<a class="ctl primary" href="../play/vs-computer.html">Play the engine</a>'
    + '<a class="ctl" href="../puzzles/">Find a mate</a>'
    + '<a class="ctl" href="../study/">Read a famous game</a>';
}

// ------------------------------------------------------------------- the list

/**
 * The name on a chip, which should be one short word or two: "The pawn" is "Pawn"
 * and "Check, and checkmate" is "Checkmate". The button's label keeps the full name.
 */
function tileName(lesson) {
  return lesson.tile || lesson.name.replace(/^The /, '').replace(/^./, (c) => c.toUpperCase());
}

function renderList() {
  $('lesson-list').innerHTML = LESSONS.map((lesson, i) => (
    `<li><button type="button" data-at="${i}" title="${escapeHtml(lesson.name)}" aria-label="${escapeHtml(lesson.name)}">`
    + `<span class="men${(LESSON_MEN[lesson.id] || '').length > 1 ? ' two' : ''}" aria-hidden="true">${menOf(lesson.id)}</span>`
    + `<span class="row-name">${escapeHtml(tileName(lesson))}</span>`
    + `<span class="tick" aria-hidden="true"></span>`
    + `</button></li>`
  )).join('');
  $('lesson-list').addEventListener('click', (event) => {
    const button = event.target.closest('button[data-at]');
    if (button) show(Number(button.dataset.at));
  });
}

/** Which row is on the board, and which are done. */
/**
 * What the account knows, once it has said — see the same block in `puzzles.js`.
 * The list is drawn from this browser first and re-marked if a sync adds to it.
 */
onMerged(() => {
  const was = done.size;
  for (const id of readDone()) done.add(id);
  if (done.size !== was) markList(false);
});
syncSoon();

function markList(scroll) {
  const rows = [...$('lesson-list').querySelectorAll('button[data-at]')];
  rows.forEach((row, i) => {
    row.classList.toggle('on', i === at);
    row.classList.toggle('did', done.has(LESSONS[i].id));
  });
  // The one on the board brought into the strip, by the strip's own scroll and
  // never `scrollIntoView`: that would move the PAGE on a phone every time Next
  // was pressed. The plain look's column scrolls down; the friendly look's strip
  // scrolls across, and there the chip is centred so its neighbours show too.
  const list = $('lesson-list');
  const row = rows[at];
  if (!scroll || !row) return;
  const box = list.getBoundingClientRect();
  const r = row.getBoundingClientRect();
  if (list.scrollWidth > list.clientWidth + 1) {
    list.scrollTo({ left: list.scrollLeft + r.left - box.left - (box.width - r.width) / 2,
      behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
  } else if (list.scrollHeight > list.clientHeight + 1 && (r.top < box.top || r.bottom > box.bottom)) {
    list.scrollTop += r.top < box.top ? r.top - box.top : r.bottom - box.bottom;
  }
}

/** "3/10" in the masthead, once there is one done; the ticks say which. */
function countUp() {
  $('done').textContent = done.size ? `${done.size}/${LESSONS.length}` : '';
  $('status').textContent = done.has(now().id) ? 'done' : '';
}

// ------------------------------------------------------------------ the buttons

$('next').addEventListener('click', () => show(at + 1));
$('again').addEventListener('click', () => show(at));
$('start').addEventListener('click', start);
$('verdict-acts').addEventListener('click', (event) => {
  if (event.target.closest('#go-next')) show(at + 1);
});

// The same three keys as the puzzles, so the two pages are one habit. Ignored
// while a modifier is down or while something is being typed into, neither of
// which happens on this page yet and both of which would be a bug if it did.
addEventListener('keydown', (event) => {
  if (event.metaKey || event.ctrlKey || event.altKey) return;
  if (event.target instanceof HTMLInputElement || event.target instanceof HTMLTextAreaElement) return;
  if (event.key === 'n') show(at + 1);
  else if (event.key === 'r') show(at);
  // Through the button and not through `setVoice`, so the key and the click are one
  // path: a toggle with two ways in is a toggle that gets out of step with the
  // thing drawn on the screen.
  else if (event.key === 'v') $('voice').click();
});
